"use client";

import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";

import Image from "next/image";

import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";
import { GroqImage } from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

import check from "@/public/frankfurt-event/check.svg";

export default function Year({
  data,
}: {
  data: PageEventSchema | PageKryptoDaySchema;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const changeCurrent = useCallback(
    (index: number) => {
      if (!api) {
        return;
      }

      api.scrollTo(index);
    },
    [api],
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <section className="relative pb-[10rem] overflow-clip lg:pb-[14rem]">
      <div className="px-[2rem] w-[min(152.5rem,100%)] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-[5rem] lg:gap-0">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center lg:items-start"
        >
          <div className="w-[min(65rem,100%)] flex flex-col items-start">
            {data?.carouselTexts && (
              <PortableText
                value={data?.carouselTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h2: ({ children }) => (
                      <h2 className="text-[4rem] lg:text-[6rem] font-semibold gradient-title !text-white/80 leading-[1.2em] mb-[2rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-white text-opacity-60 text-[2rem] w-[min(57rem,100%)] mb-[2rem]">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    strong: ({ children }) => (
                      <span className="text-white text-[2rem]">{children}</span>
                    ),
                  },
                })}
              />
            )}

            <p className="text-white text-[2rem] w-[min(57rem,100%)] mb-[3rem]">
              Deine Möglichkeiten vor Ort
            </p>

            <div className="flex flex-col gap-[1.5rem]">
              {data?.carouselBullets &&
                data?.carouselBullets.map((bullet, index) => (
                  <div key={index} className="w-full">
                    <div className="flex items-center gap-[2rem] mb-[2rem]">
                      <Image
                        className="!w-[5rem] min-w-[5rem] max-w-[5rem]"
                        src={check}
                        alt=""
                      />
                      <p className="mb-0 text-white text-opacity-60">
                        {bullet}
                      </p>
                    </div>
                    {data?.carouselBullets?.length !== index + 1 && (
                      <div className="w-full border-t opacity-20 [border-image:repeating-linear-gradient(to_right,white_0,white_10px,transparent_10px,transparent_20px)_1]"></div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col justify-center items-center"
        >
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 5000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
              }),
            ]}
            className="w-full max-w-[72rem]"
          >
            <CarouselContent className="!-ml-2 lg:-ml-4">
              {data?.carouselPhotos?.map((photo, index) => (
                <CarouselItem key={index} className="!pl-2 lg:!pl-4">
                  <div className="relative w-full lg:p-1 max-w-[50rem] lg:max-w-[70rem]">
                    <GroqImage
                      src={photo}
                      alt="photo"
                      className="!w-full !h-full"
                      quality={100}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* constrols */}
          <div className="flex flex-col justify-center items-center w-full max-w-[70rem] gap-[8rem] mt-[1rem] lg:mt-[2rem]">
            <div className="flex justify-center items-center w-full gap-[.25rem]">
              <div className="flex justify-center items-stretch w-full gap-[0.5rem]">
                {data?.carouselPhotos?.map((photo, index) => {
                  return (
                    <button
                      key={index}
                      onClick={() => changeCurrent(index)}
                      className={`${
                        index === current
                          ? "lg:!w-[8.5rem] border-[#61FFCE] opacity-100"
                          : "border-[#757575] opacity-50"
                      } !w-[6.9rem] border-[.25rem] rounded-[.5rem] transition-all`}
                    >
                      <Image
                        src={photo}
                        alt="photo"
                        className="!w-full !h-[5rem] lg:!h-[6rem] object-cover rounded-[.5rem] overflow-clip"
                        quality={100}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {/* end constrols */}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full flex justify-center items-center mt-[4rem] lg:mt-[6rem] [&_*]:font-sans [&_*]:uppercase"
      >
        {data?.carouselCTA && (
          <CTAButton
            data={data?.carouselCTA}
            className="[&_.cta]:!py-[2.5rem]"
          />
        )}
      </motion.div>
    </section>
  );
}
